import { z } from 'zod';
import {
  hashSchema,
  platformSchema,
  slugSchema,
  timestampSchema,
  ulidSchema,
  publicUserSchema,
} from './common.js';
import { gameKindSchema, buildStatusSchema } from './library.js';
import { accentColorSchema } from './accent.js';
import { LOGO_WIDTH_MAX, LOGO_WIDTH_MIN, logoAnchorSchema, MAX_SCREENSHOTS } from './artwork.js';
import { MAX_TRAILERS, trailerContentTypeSchema } from './trailers.js';
import { artworkSlotSchema } from './artwork.js';
import {
  gameFeaturesSchema,
  gameGenresSchema,
  gameLanguagesSchema,
  releaseDateSchema,
  systemRequirementsSchema,
} from './gameDetails.js';
import { storeUrlSchema } from './stores.js';

/* ------------------------------------------------------------------ games */

export const DEFAULT_IGNORE_GLOBS = ['*.pdb', 'Saved/**', '*.log'] as const;

/**
 * An optional line of prose on a game, stored trimmed with empty turned into null.
 *
 * The same rule `normalizeStoreUrl` applies to the storefront fields, for the same reason: a
 * field somebody cleared and a field nobody ever filled in are the same thing, and letting both
 * reach the column means every screen that draws it has to test for both.
 */
function nullableText(max: number) {
  return z
    .string()
    .max(max)
    .nullable()
    .optional()
    .transform((value) => {
      const trimmed = (value ?? '').trim();
      return trimmed === '' ? null : trimmed;
    });
}

export const createGameBodySchema = z.object({
  slug: slugSchema,
  title: z.string().trim().min(1).max(120),
  kind: gameKindSchema,
  shortDescription: z.string().max(300).default(''),
  /**
   * The Steamworks "About This Game" text, in Steam store BBCode.
   *
   * Never validated as markup and never normalised: an unknown tag is the admin's business and
   * a description that came out of Steamworks has to go back in unchanged. `bbcode.ts` renders
   * what it recognises and leaves the rest as text.
   */
  descriptionBb: z.string().max(50000).default(''),
  androidPackageName: z
    .string()
    .regex(/^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/i, 'not an Android package name')
    .nullable()
    .optional(),
  /** Optional, and checked against the storefront's own address shape. See `stores.ts`. */
  steamUrl: storeUrlSchema('steam'),
  epicUrl: storeUrlSchema('epic'),
  /**
   * Which product on each storefront this game is, so somebody who already owns it there can
   * claim it here.
   *
   * Deliberately not derived from the two addresses above. An address is a marketing link and is
   * allowed to point at a bundle, a regional page or a redirect; these are the ids Steam and
   * Epic answer an ownership question about, and guessing one from the other is how a claim ends
   * up asking about the wrong product and being told, correctly, no.
   *
   * All three optional, and the Epic pair is all-or-nothing — a namespace without an item id
   * identifies nothing. `storeProductProblem` is what says so, on the form and again on save.
   */
  sortOrder: z.number().int().default(0),

  /* ---- the facts panel. Every one optional: a game is created with a name and filled in
     afterwards, and a create form that asked for a graphics card would never be used. ---- */

  developerName: nullableText(120),
  publisherName: nullableText(120),
  /** Not a storefront — those are `steamUrl` and `epicUrl`, and they have their own shapes. */
  websiteUrl: nullableText(500),
  /** `YYYY`, `YYYY-MM` or `YYYY-MM-DD`. See `releaseDateSchema`. */
  releaseDate: releaseDateSchema,
  releaseNote: nullableText(120),
  genres: gameGenresSchema.optional(),
  features: gameFeaturesSchema.optional(),
  languages: gameLanguagesSchema.optional(),
  systemRequirements: systemRequirementsSchema.optional(),
  /**
   * What this game costs to own outright, in cents, or null for one that is not sold.
   *
   * Cents because the rest of the system is: 1999 is EUR 19.99. Sending 19.99 would be a request
   * to charge nineteen cents, and the difference is not something a rounding bug should decide.
   *
   * Only a `game` or an `early_access` can carry one; the API refuses a price on a `dev` or a
   * `prototype` rather than storing one nothing will ever read.
   */
  contentNotice: nullableText(2000),
  legalNotice: nullableText(2000),
});
export type CreateGameBody = z.infer<typeof createGameBodySchema>;

export const updateGameBodySchema = createGameBodySchema.partial().extend({
  /** The `header` slot: Steam's header capsule. Named `cover` since before the slots existed. */
  coverImageId: ulidSchema.nullable().optional(),
  /** The `hero` slot: Steam's library hero. */
  heroImageId: ulidSchema.nullable().optional(),
  /** The `capsule` slot: Steam's portrait library capsule, which draws the shelf tile. */
  capsuleImageId: ulidSchema.nullable().optional(),
  /** The `logo` slot: the title as artwork, drawn over the hero. */
  logoImageId: ulidSchema.nullable().optional(),
  logoAnchor: logoAnchorSchema.optional(),
  logoWidthPct: z.number().int().min(LOGO_WIDTH_MIN).max(LOGO_WIDTH_MAX).optional(),
  /** The `small` slot: Steam's small capsule, for rows a header capsule is too tall for. */
  smallImageId: ulidSchema.nullable().optional(),
  /** The `background` slot: what sits behind the game page. */
  backgroundImageId: ulidSchema.nullable().optional(),
  /**
   * The colour the room takes from this game, sent with the artwork it was read off.
   *
   * The admin panel extracts it from the file it is already holding, so it arrives in the same
   * patch as the image id rather than in a second request — and clearing that image sends null,
   * because an accent outliving the picture it came from is a colour nobody chose.
   */
  accentColor: accentColorSchema.nullable().optional(),
  ignoreGlobs: z.array(z.string().max(200)).max(100).optional(),
  devUserIds: z.array(ulidSchema).max(50).optional(),
  published: z.boolean().optional(),
});
export type UpdateGameBody = z.infer<typeof updateGameBodySchema>;

/* ------------------------------------------------------------------ builds */

export const chunkingParamsSchema = z.object({
  algorithm: z.literal('fastcdc2020'),
  min: z.number().int(),
  avg: z.number().int(),
  max: z.number().int(),
  zstdLevel: z.number().int(),
});
export type ChunkingParams = z.infer<typeof chunkingParamsSchema>;

/** Values from `04-launcher-and-patcher.md` section 3.1. Kept here so both ends agree. */
export const DEFAULT_CHUNKING: ChunkingParams = {
  algorithm: 'fastcdc2020',
  min: 512 * 1024,
  avg: 2 * 1024 * 1024,
  max: 8 * 1024 * 1024,
  zstdLevel: 9,
};

export const createBuildBodySchema = z.object({
  gameId: ulidSchema,
  platform: platformSchema,
  versionLabel: z.string().trim().min(1).max(64),
});

export const createBuildResponseSchema = z.object({
  buildId: ulidSchema,
  chunking: chunkingParamsSchema,
});

export const missingChunksBodySchema = z.object({
  hashes: z.array(hashSchema).min(1).max(2000),
});

export const missingChunksResponseSchema = z.object({
  missing: z.array(z.object({ hash: hashSchema, uploadUrl: z.string().url() })),
  expiresAt: timestampSchema,
});

export const buildManifestUrlsResponseSchema = z.object({
  manifestUploadUrl: z.string().url(),
  manifestKey: z.string(),
  chunkListUploadUrl: z.string().url(),
  chunkListKey: z.string(),
  expiresAt: timestampSchema,
});

export const completeBuildBodySchema = z.object({
  totalBytes: z.number().int().nonnegative(),
  fileCount: z.number().int().nonnegative(),
  chunkCount: z.number().int().nonnegative(),
  newBytes: z.number().int().nonnegative(),
});

export const adminBuildSchema = z.object({
  id: ulidSchema,
  gameId: ulidSchema,
  platform: platformSchema,
  status: buildStatusSchema,
  versionLabel: z.string(),
  totalBytes: z.number().int().nullable(),
  fileCount: z.number().int().nullable(),
  chunkCount: z.number().int().nullable(),
  newBytes: z.number().int().nullable(),
  uploadedBy: publicUserSchema.nullable(),
  createdAt: timestampSchema,
  publishedAt: timestampSchema.nullable(),
  error: z.string().nullable(),
});
export type AdminBuild = z.infer<typeof adminBuildSchema>;

/* ------------------------------------------------------------------ media */

export const createImageBodySchema = z
  .object({
    bucket: z.enum(['public', 'content']),
    contentType: z.enum(['image/png', 'image/jpeg', 'image/webp', 'image/avif']),
    width: z.number().int().positive().max(20000).optional(),
    height: z.number().int().positive().max(20000).optional(),
    /**
     * Which piece of game artwork this is, when it is one. Naming a slot puts the image
     * through the size rules in `artwork.ts`, which is why the dimensions stop being optional
     * here: a slot with nothing to measure would be a check that quietly never ran.
     */
    slot: artworkSlotSchema.optional(),
  })
  .refine((body) => body.slot === undefined || (body.width && body.height), {
    message: 'artwork needs its width and height',
    path: ['slot'],
  });

export const createImageResponseSchema = z.object({
  imageId: ulidSchema,
  uploadUrl: z.string().url(),
  key: z.string(),
  expiresAt: timestampSchema,
});

/* -------------------------------------------------------------- screenshots */

/**
 * Adding one picture to a game's gallery.
 *
 * The image is uploaded first, the same way every other slot is, and this attaches it. Order
 * is set separately, because dragging a gallery about should not re-upload anything.
 */
export const addScreenshotBodySchema = z.object({
  imageId: ulidSchema,
  caption: z.string().trim().max(200).nullable().optional(),
});

export const reorderScreenshotsBodySchema = z.object({
  /** Every screenshot on the game, in the order they should draw. */
  ids: z.array(ulidSchema).max(MAX_SCREENSHOTS),
});

/**
 * Asking for a presigned upload target for a trailer video.
 *
 * `trailerProblem` checks the same content type and size on the way in, because a presigned URL
 * is a write into the public bucket and anything that can ask for one could ask for a 4 GB one.
 */
export const createVideoBodySchema = z.object({
  contentType: trailerContentTypeSchema,
  sizeBytes: z.number().int().positive(),
  durationMs: z.number().int().positive().optional(),
});

/** Attaching an uploaded video to a game as a trailer. */
export const addTrailerBodySchema = z.object({
  videoId: ulidSchema,
  /** The still shown before it plays. Optional: the player falls back to the first frame. */
  posterImageId: ulidSchema.nullable().optional(),
  title: z.string().trim().max(120).nullable().optional(),
  durationMs: z.number().int().positive().nullable().optional(),
});

/** The name on a trailer and the still in front of it. Neither one re-uploads the video. */
export const updateTrailerBodySchema = z.object({
  title: z.string().trim().max(120).nullable().optional(),
  posterImageId: ulidSchema.nullable().optional(),
});

export const reorderTrailersBodySchema = z.object({
  /** Every trailer on the game, in the order they should draw. */
  ids: z.array(ulidSchema).max(MAX_TRAILERS),
});

/* ------------------------------------------------------------------ roles and audit */

/**
 * Roles an admin can hand out.
 *
 * `admin` is not here on purpose: it comes from the ADMIN_DISCORD_IDS secret and is applied at
 * sign in, so there is no endpoint that can grant it and no row that can be edited into it.
 */
export const grantableRoleSchema = z.enum(['member', 'dev']);

export const setRoleBodySchema = z.object({
  username: z.string().min(1).max(64),
  role: grantableRoleSchema,
  gameIds: z.array(ulidSchema).max(200).optional(),
});

export const auditEntrySchema = z.object({
  id: ulidSchema,
  actor: publicUserSchema.nullable(),
  action: z.string(),
  targetType: z.string(),
  targetId: z.string(),
  data: z.record(z.unknown()).nullable(),
  createdAt: timestampSchema,
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;

export const webhookEventSchema = z.object({
  id: z.string(),
  type: z.string(),
  receivedAt: timestampSchema,
  processedAt: timestampSchema.nullable(),
  attempts: z.number().int(),
  error: z.string().nullable(),
});
export type WebhookEvent = z.infer<typeof webhookEventSchema>;

export const storageRowSchema = z.object({
  gameId: ulidSchema,
  slug: slugSchema,
  title: z.string(),
  storedBytes: z.number().int(),
  objectCount: z.number().int(),
  lastGcAt: timestampSchema.nullable(),
  lastGcRemovedObjects: z.number().int().nullable(),
  lastGcRemovedBytes: z.number().int().nullable(),
});
export type StorageRow = z.infer<typeof storageRowSchema>;

export const adminOverviewSchema = z.object({
  membersCount: z.number().int(),
  failedWebhooks: z.number().int(),
  pendingTasks: z.array(
    z.object({ id: ulidSchema, action: z.string(), createdAt: timestampSchema, note: z.string() }),
  ),
  lastBuilds: z.array(adminBuildSchema),
});
export type AdminOverview = z.infer<typeof adminOverviewSchema>;

/* --------------------------------------------------------------- API keys */

/**
 * An API key as the dashboard sees it. The secret is NOT here and cannot be: only its SHA-256 is
 * stored, so `prefix` — the first few characters — is all there is to tell two keys apart by.
 */
export const apiKeySummarySchema = z.object({
  id: ulidSchema,
  name: z.string(),
  prefix: z.string(),
  createdAt: timestampSchema,
  lastUsedAt: timestampSchema.nullable(),
  expiresAt: timestampSchema.nullable(),
  revokedAt: timestampSchema.nullable(),
  /** The key this one replaced, when it came from a rotation. */
  rotatedFrom: ulidSchema.nullable(),
});
export type ApiKeySummary = z.infer<typeof apiKeySummarySchema>;

export const createApiKeyBodySchema = z.object({
  name: z.string().trim().min(1).max(64),
});

/**
 * The one response that ever carries a secret.
 *
 * Shaped as its own type precisely so that returning it from anywhere else looks wrong: every
 * other endpoint answers with `ApiKeySummary`.
 */
export const createdApiKeySchema = z.object({
  key: z.string(),
  apiKey: apiKeySummarySchema,
});
export type CreatedApiKeyResponse = z.infer<typeof createdApiKeySchema>;

/* -+ game currency -+ */

/**
 * The currencies a game can be priced in.
 *
 * `updateGameBodySchema` takes the same two. A price stored in a third currency against a
 * column that only means EUR or USD would put the wrong symbol on the store page.
 */
export const GAME_CURRENCIES = ['EUR', 'USD'] as const;
export type GameCurrency = (typeof GAME_CURRENCIES)[number];

export function isGameCurrency(value: string | null | undefined): value is GameCurrency {
  return value === 'EUR' || value === 'USD';
}
